

const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Synonym dictionary for section headers. Add more variants here if needed.
const SECTION_SYNONYMS = {
  summary: ['summary', 'profile', 'about', 'objective', 'professional summary', 'career objective'],
  skills: [
    'skills', 'skill', 'technical skills', 'technical skill', 'core competencies',
    'key skills', 'key skill', 'skill set', 'skillset', 'expertise',
  ],
  experience: [
    'experience', 'work experience', 'professional experience', 'employment history',
    'career history', 'work history', 'projects', 'project description', 'key projects',
    'projects description',
  ],
  education: ['education', 'academic background', 'qualifications', 'academics', 'educational background'],
  links: ['links', 'portfolio', 'social', 'contact'],
};

// If `line` starts with a known section-header phrase (even if more text
// follows on the same line, e.g. "PROFESSIONAL EXPERIENCE: Software Engineer…"),
// returns { key, rest } where `rest` is whatever trailing text remains after
// the header words. Returns null if the line isn't a header.
function matchHeaderPrefix(line) {
  const words = line.trim().split(/\s+/);
  for (const [key, syns] of Object.entries(SECTION_SYNONYMS)) {
    // Check longer synonyms first so "key skills" wins over "skills" etc.
    const sorted = [...syns].sort((a, b) => b.split(' ').length - a.split(' ').length);
    for (const syn of sorted) {
      const synWordCount = syn.split(' ').length;
      if (words.length < synWordCount) continue;
      const prefix = words.slice(0, synWordCount).join(' ');
      if (normalize(prefix) === syn) {
        let rest = words.slice(synWordCount).join(' ');
        rest = rest.replace(/^[:\-–\s]+/, '').trim();
        return { key, rest };
      }
    }
  }
  return null;
}

// Splits raw text into named sections based on detected headers.
// `top` = everything before the first detected section (personal info block).
function splitIntoSections(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim());
  const sections = { summary: [], skills: [], experience: [], education: [], links: [], top: [] };
  let current = 'top';

  lines.forEach((line) => {
    if (!line) return;
    const headerMatch = matchHeaderPrefix(line);
    if (headerMatch) {
      current = headerMatch.key;
      if (headerMatch.rest) sections[current].push(headerMatch.rest);
      return;
    }
    sections[current].push(line);
  });

  return sections;
}

// ---------- Field extractors ----------

function extractEmail(text) {
  const match = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return match ? match[0] : '';
}

function extractPhone(text) {
  // Matches Indian + generic international formats: +91 99001 23456, 9900123456, (022) 1234-5678 etc.
  const match = text.match(/(\+?\d{1,3}[\s-]?)?(\(?\d{3,5}\)?[\s-]?)?\d{3,4}[\s-]?\d{3,4}\b/);
  if (!match) return '';
  const digits = match[0].replace(/[^\d+]/g, '');
  return digits.length >= 7 ? match[0].trim() : '';
}

const KNOWN_CITIES = [
  'new delhi', 'delhi', 'noida', 'gurgaon', 'gurugram', 'mumbai', 'bengaluru', 'bangalore',
  'pune', 'hyderabad', 'chennai', 'kolkata', 'ahmedabad', 'jaipur', 'lucknow', 'chandigarh',
  'indore', 'kochi', 'cochin', 'bareilly', 'remote', 'work from home',
];

function extractLocation(topLines) {
  const topText = topLines.join(' ');
  for (const city of KNOWN_CITIES) {
    const re = new RegExp(`\\b${city.replace(/\s+/g, '\\s+')}\\b`, 'i');
    const m = topText.match(re);
    if (m) return m[0].replace(/\s+/g, ' ').trim();
  }
  // fallback: a line like "City, Country" pattern (two comma separated short words)
  const cityCountry = topLines.find((l) => /^[A-Za-z\s]+,\s*[A-Za-z\s]+$/.test(l.trim()) && l.length < 40);
  return cityCountry ? cityCountry.trim() : '';
}

function extractLinks(text) {
  const urls = text.match(/(https?:\/\/[^\s,]+|www\.[^\s,]+|linkedin\.com\/[^\s,]+|github\.com\/[^\s,]+)/gi) || [];
  return [...new Set(urls.map((u) => u.replace(/[.,]$/, '')))].join(', ');
}

function extractName(topLines) {
  for (const line of topLines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.includes('@')) continue; // skip email line
    if (/\d{4,}/.test(trimmed)) continue; // skip phone/number-heavy lines
    if (normalize(trimmed).split(' ').length > 5) continue; // too long to be a name
    if (/resume|curriculum vitae|cv\b/i.test(trimmed)) continue;
    const words = trimmed.split(/\s+/);
    if (words.length >= 1 && words.length <= 4 && /^[A-Za-z.\s]+$/.test(trimmed)) {
      return trimmed;
    }
  }
  return '';
}

function extractSkills(skillsLines) {
  const joined = skillsLines.join(', ');
  if (!joined.trim()) return [];
  const parts = joined
    .split(/[,•|\u2022\u00b7\/\n:]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 1 && s.length < 35);
  return [...new Set(parts)];
}

const DEGREE_KEYWORDS = [
  'b.tech', 'btech', 'b.e', 'be ', 'bachelor', 'bachler', 'm.tech', 'mtech', 'master', 'mba',
  'mca', 'bca', 'b.sc', 'bsc', 'm.sc', 'msc', 'diploma', 'phd', 'ph.d', '12th', '10th', 'intermediate', 'high school',
];

// Month-year on either side, OR a bare year; end side also accepts
// "present" / "current" / "till now" / "tillnow" (no space, a common
// PDF text-extraction artifact when a resume literally says "Till Now").
const DATE_RANGE_REGEX =
  /((?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s*\d{4}|\d{4})\s*(?:–|-|to)\s*((?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s*\d{4}|present|current|till\s*now|\d{4})/i;

// Treats each line that contains a date range as one job/project entry —
// works whether the resume uses multi-line blocks or single dense lines
// like "Software Engineer – Bizbrolly Solutions Pvt. Ltd.  Feb2022 – Till Now".
function extractExperience(expLines) {
  const entries = [];

  expLines.forEach((line) => {
    if (!line.trim()) return;
    const dateMatch = line.match(DATE_RANGE_REGEX);
    if (!dateMatch) return; // skip bullet/description lines with no date

    const before = line.slice(0, dateMatch.index).replace(/[•\u2022]/g, '').trim();
    // Split "Role – Company" on a dash that isn't part of a date itself
    const parts = before.split(/\s[–-]\s|[–-](?=[A-Z])/).filter(Boolean);

    let role = '';
    let company = '';
    if (parts.length >= 2) {
      role = parts[0].trim().replace(/[-:]+$/, '');
      company = parts.slice(1).join(' - ').trim();
      company = company.split(',')[0].trim(); // drop trailing location junk
    } else {
      role = before.replace(/[-:]+$/, '').trim();
    }

    const current = /present|current|till\s*now/i.test(dateMatch[0]);
    if (!role && !company) return;
    entries.push({ Role: role, Company: company, Dates: dateMatch[0].trim(), Current: current });
  });

  return entries;
}

// Treats each line containing a degree keyword OR a 4-digit year as one
// education entry, splitting "Degree – School [Year]" on the dash.
function extractEducation(eduLines) {
  const entries = [];

  eduLines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const hasDegree = DEGREE_KEYWORDS.some((k) => normalize(trimmed).includes(normalize(k)));
    const yearMatch = trimmed.match(/\b(19|20)\d{2}\b/);
    if (!hasDegree && !yearMatch) return; // skip declarations, "Place:", etc.

    const dashParts = trimmed.split(/\s[–-]\s|[–-](?=[A-Z])/).filter(Boolean);
    let degree = '';
    let school = '';
    if (dashParts.length >= 2) {
      degree = dashParts[0].trim();
      school = dashParts.slice(1).join(' - ').trim();
    } else {
      degree = trimmed;
    }
    // strip a trailing bracketed/plain year from the school text for cleanliness
    school = school.replace(/[\[(]?\b(19|20)\d{2}\b[\])]?/, '').trim();

    entries.push({ Degree: degree, School: school, Dates: yearMatch ? yearMatch[0] : '' });
  });

  return entries;
}

function extractTotalExperienceYears(experiences) {
  let totalMonths = 0;
  experiences.forEach((exp) => {
    const m = (exp.Dates || '').match(DATE_RANGE_REGEX);
    if (!m) return;
    const startYear = (m[1].match(/\d{4}/) || [])[0];
    const endRaw = m[2];
    const endYear = /present|current|till\s*now/i.test(endRaw) ? new Date().getFullYear() : (endRaw.match(/\d{4}/) || [])[0];
    if (startYear && endYear) {
      totalMonths += (parseInt(endYear, 10) - parseInt(startYear, 10)) * 12;
    }
  });
  return totalMonths > 0 ? Math.round((totalMonths / 12) * 10) / 10 : '';
}

// Matches "4.6+ years of hands-on experience", "5 years experience",
// "3+ years of professional experience" etc. — allows a few filler
// words between "years" and "experience".
function extractExplicitExperienceYears(text) {
  const m = text.match(/(\d+(?:\.\d+)?)\s*\+?\s*years?\s+(?:[a-zA-Z-]+\s+){0,4}experience/i);
  return m ? parseFloat(m[1]) : null;
}

// ---------- Main entry point ----------

/**
 * Parses raw resume text into structured candidate fields.
 * Every field defaults to '' / [] when nothing is confidently matched —
 * no placeholder/demo values are ever injected.
 */
export function parseResumeText(rawText) {
  const text = (rawText || '').replace(/\u0000/g, '');
  const sections = splitIntoSections(text);

  const email = extractEmail(text);
  const phone = extractPhone(text);
  const name = extractName(sections.top);
  const [firstName, ...rest] = name ? name.split(/\s+/) : [''];
  const lastName = rest.join(' ');

  const location = extractLocation(sections.top.length ? sections.top : text.split(/\r?\n/).slice(0, 15));
  const links = extractLinks(text);
  const skills = extractSkills(sections.skills);
  const experiences = extractExperience(sections.experience);
  const educations = extractEducation(sections.education);
  const about = sections.summary.filter((l) => l.trim()).join(' ').trim();

  const explicitYears = extractExplicitExperienceYears(text);
  const totalExperienceYears = explicitYears ?? extractTotalExperienceYears(experiences);

  // The first "Current" entry in document order is normally the actual job
  // (it's listed before individual project entries on real resumes).
  const currentExp = experiences.find((e) => e.Current) || experiences[0] || null;

  return {
    FirstName: firstName || '',
    LastName: lastName || '',
    Email: email || '',
    Phone: phone || '',
    Location: location || '',
    CurrentRole: currentExp?.Role || '',
    CurrentCompany: currentExp?.Company || '',
    TotalExperienceYears: totalExperienceYears === '' ? '' : totalExperienceYears,
    AboutSummary: about || '',
    SkillsPrimary: skills.slice(0, Math.ceil(skills.length / 2)).join(', '),
    SkillsOther: skills.slice(Math.ceil(skills.length / 2)).join(', '),
    Links: links || '',
    Experiences: experiences,
    Educations: educations,
  };
}